import { Component } from '@angular/core';
import Swal from 'sweetalert2';
import { RegistroBeneficiariosService } from '../../services/registro-beneficiarios.service';
import {
  BeneficiarioConDueno,
  BeneficiarioNormalizado,
  BeneficiarioOrigen,
  BeneficiariosPorUsuario,
  PageMeta,
  UsuarioResultado,
} from '../../interfaces/registro-beneficiarios.interface';

type Modo = 'cliente' | 'beneficiario';

/**
 * Consulta de solo lectura del registro de beneficiarios para operadores.
 *
 * Dos direcciones de búsqueda:
 *  - `cliente`: busca al usuario por RUT/correo/ID y permite ver sus
 *    beneficiarios (drill-down agrupado por origen).
 *  - `beneficiario`: busca directamente sobre los beneficiarios y muestra el
 *    cliente dueño (caso "identificar al remitente de una transferencia
 *    errónea").
 *
 * Sin acciones de mutación: es una pantalla de consulta.
 */
@Component({
  selector: 'app-registro-beneficiarios',
  templateUrl: './registro-beneficiarios.component.html',
  styleUrls: ['./registro-beneficiarios.component.sass'],
})
export class RegistroBeneficiariosComponent {
  modo: Modo = 'cliente';
  q = '';
  origen: BeneficiarioOrigen | '' = '';

  loading = false;
  buscado = false;

  // Modo cliente.
  usuarios: UsuarioResultado[] = [];
  usuariosMeta: PageMeta | null = null;

  // Modo beneficiario.
  beneficiarios: BeneficiarioConDueno[] = [];
  beneficiariosMeta: PageMeta | null = null;

  // Drill-down: beneficiarios de un cliente seleccionado.
  drill: BeneficiariosPorUsuario | null = null;
  drillLoading = false;
  drillUsuario: UsuarioResultado | null = null;

  // IDs de beneficiarios Vita con un re-chequeo en curso (spinner por fila).
  refrescandoVita = new Set<string>();

  private page = 1;
  private readonly limit = 20;

  readonly origenes: { value: BeneficiarioOrigen; label: string; icon: string }[] = [
    { value: 'bancario', label: 'Bancario', icon: 'account_balance' },
    { value: 'vita', label: 'Vita', icon: 'public' },
    { value: 'zelle', label: 'Zelle', icon: 'attach_money' },
    { value: 'cripto', label: 'Cripto', icon: 'currency_bitcoin' },
  ];

  constructor(private service: RegistroBeneficiariosService) {}

  setModo(modo: Modo): void {
    if (this.modo === modo) return;
    this.modo = modo;
    this.resetResultados();
  }

  async buscar(resetPage = true): Promise<void> {
    if (!this.q.trim()) {
      Swal.fire('Falta el criterio', 'Ingresa un RUT, correo o ID para buscar.', 'info');
      return;
    }
    if (resetPage) this.page = 1;

    this.loading = true;
    this.buscado = true;
    this.cerrarDrill();

    try {
      if (this.modo === 'cliente') {
        const res = await this.service.searchUsers({
          q: this.q.trim(),
          page: this.page,
          limit: this.limit,
        });
        this.usuarios = res.data;
        this.usuariosMeta = res.meta;
      } else {
        const res = await this.service.searchBeneficiarios({
          q: this.q.trim(),
          origen: this.origen || undefined,
          page: this.page,
          limit: this.limit,
        });
        this.beneficiarios = res.data;
        this.beneficiariosMeta = res.meta;
      }
    } catch (err) {
      this.mostrarError(err);
    } finally {
      this.loading = false;
    }
  }

  async verBeneficiarios(usuario: UsuarioResultado): Promise<void> {
    this.drillUsuario = usuario;
    this.drill = null;
    this.drillLoading = true;
    try {
      this.drill = await this.service.getByUser(usuario.usuarioId);
    } catch (err) {
      this.mostrarError(err);
      this.drillUsuario = null;
    } finally {
      this.drillLoading = false;
    }
  }

  cerrarDrill(): void {
    this.drill = null;
    this.drillUsuario = null;
    this.drillLoading = false;
    this.refrescandoVita.clear();
  }

  esRefrescandoVita(b: BeneficiarioNormalizado): boolean {
    return this.refrescandoVita.has(String(b.beneficiarioId));
  }

  /**
   * Re-verifica el estado AML de un beneficiario Vita contra Vita y refleja el
   * resultado en la fila. NO fuerza desbloqueo: si Vita lo sigue teniendo
   * `banned`, queda `banned` (el desbloqueo lo resuelve el compliance de Vita).
   */
  async refrescarVita(b: BeneficiarioNormalizado): Promise<void> {
    const id = String(b.beneficiarioId);
    if (this.refrescandoVita.has(id)) return;
    this.refrescandoVita.add(id);
    try {
      const res = await this.service.refreshVita(id);
      b.vitaEstado = res.vitaEstado;
      if (res.vitaEstado === 'banned') {
        Swal.fire(
          'Sigue bloqueado',
          'Vita mantiene a este beneficiario como bloqueado. El desbloqueo depende del área de cumplimiento de Vita.',
          'warning',
        );
      } else {
        Swal.fire('Habilitado', 'El beneficiario quedó habilitado en Vita.', 'success');
      }
    } catch (err) {
      this.mostrarError(err);
    } finally {
      this.refrescandoVita.delete(id);
    }
  }

  async paginar(delta: number): Promise<void> {
    const meta = this.modo === 'cliente' ? this.usuariosMeta : this.beneficiariosMeta;
    if (!meta) return;
    const next = this.page + delta;
    if (next < 1 || next > meta.totalPages) return;
    this.page = next;
    await this.buscar(false);
  }

  get metaActual(): PageMeta | null {
    return this.modo === 'cliente' ? this.usuariosMeta : this.beneficiariosMeta;
  }

  origenLabel(origen: BeneficiarioOrigen): string {
    return this.origenes.find((o) => o.value === origen)?.label ?? origen;
  }

  origenIcon(origen: BeneficiarioOrigen): string {
    return this.origenes.find((o) => o.value === origen)?.icon ?? 'person';
  }

  private resetResultados(): void {
    this.usuarios = [];
    this.usuariosMeta = null;
    this.beneficiarios = [];
    this.beneficiariosMeta = null;
    this.buscado = false;
    this.page = 1;
    this.cerrarDrill();
  }

  private mostrarError(err: any): void {
    const status = err?.status;
    const msg =
      status === 401
        ? 'Sesión expirada o sin permisos de operador.'
        : 'Ocurrió un error al consultar. Intenta nuevamente.';
    Swal.fire('Error', msg, 'error');
  }
}
